import React, { Fragment, useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Menu, Transition } from "@headlessui/react";
import {
  BuildingOfficeIcon,
  EnvelopeIcon,
  CalendarDaysIcon,
  EnvelopeOpenIcon,
  ArrowRightOnRectangleIcon,
  UserIcon,
} from "@heroicons/react/20/solid";
import {
  ChartPieIcon,
  ClockIcon,
  KeyIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import { baseUrl } from "./DataServices/Configuration";
import { fetchTimelineUnits } from "./DataServices/EventHistoryDataService";
 
export const baseTabs = [
  { name: "Home", href: "/home", icon: ChartPieIcon, current: true },
  {
    name: "Listing",
    href: "/listing",
    icon: BuildingOfficeIcon,
    current: true,
  },
  {
    name: "Move Out",
    href: "/rented-listing",
    icon: BuildingOfficeIcon,
    current: true,
  },
  {
    name: "Calendar",
    href: "/calendar",
    icon: CalendarDaysIcon,
    current: false,
  },
  {
    name: "Automatic Response History",
    href: "/AutomaticResponseHistory",
    icon: EnvelopeOpenIcon,
    current: false,
  },
  {
    name: "Tenant details",
    href: "/members-details",
    icon: EnvelopeIcon,
    current: false,
  },
  {
    name: "Renewal",
    href: "/renewal",
    icon: CalendarDaysIcon,
    current: false,
  },
  {
    name: "Timeline",
    href: "/timeline",
    icon: ClockIcon,
    current: false,
  },
];
 
export const primaryUserTabs = [
  {
    name: "Users",
    href: "/users",
    icon: UserIcon,
    current: false,
  },
];

// Grouped under the "Lease" dropdown in the desktop nav instead of sitting inline — cuts the top-
// level row from 8 items down to 6, which is what actually fits without scrolling.
const LEASE_GROUP_NAMES = ["Listing", "Move Out", "Calendar"];

function classNames(...classes: any) {
  return classes.filter(Boolean).join(" ");
}
interface UserInfo {
  id: string;
  userName: string;
  isPrimaryUser: boolean;
  token: string;
}
 
type NavbarProps = {
  children: React.ReactNode;
  setToken: React.Dispatch<React.SetStateAction<string | null>>;
};
 
const Navbar: React.FC<NavbarProps> = ({ children, setToken }) => {
  const [selectedTab, setSelectedTab] = useState("");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
 
  const decodeJWT = (token: string): any | null => {
    try {
      // Validate JWT structure first (must have 3 parts: header.payload.signature)
      const parts = token.split(".");
      if (parts.length !== 3) {
        // Token is likely opaque (e.g., Google token), not a standard JWT
        return null;
      }

      // JWT uses base64url encoding, not standard base64
      const base64UrlDecode = (str: string) => {
        str = str.replace(/-/g, "+").replace(/_/g, "/");
        while (str.length % 4) {
          str += "=";
        }
        return atob(str);
      };

      const payload = JSON.parse(base64UrlDecode(parts[1]));
      return payload;
    } catch (error) {
      // Token is likely opaque or corrupted, silently return null for graceful fallback
      return null;
    }
  };
 
  const [userInfo, setUserInfo] = useState<UserInfo | null>(() => {
    const token = localStorage.getItem("token");
    if (token) {
      const payload = decodeJWT(token);
      if (payload) {
        // Check if it's a Google token
        const isGoogleToken = payload.iss && payload.iss.includes("google.com");
 
        let isPrimaryUser = false; // Default to false
        if (isGoogleToken) {
          isPrimaryUser = true; // Google users are always primary
        } else if (payload.isPrimaryUser !== undefined) {
          isPrimaryUser = payload.isPrimaryUser.toLowerCase() === "true";
        }
 
        return {
          id:
            payload.nameid || payload.sub || payload.id || "authenticated-user",
          userName:
            payload.name || payload.unique_name || payload.userName || "User",
          isPrimaryUser: isPrimaryUser,
          token: token,
        };
      } else {
        // Assume Google auth (opaque token) - Google users are primary
        return {
          id: "google-user",
          userName: "Google User",
          isPrimaryUser: true,
          token: token,
        };
      }
    }
    return null;
  });
 
  // Get available tabs based on user permissions
  const getAvailableTabs = () => {
    let availableTabs = [...baseTabs];
    if (userInfo?.isPrimaryUser) {
      availableTabs = [...availableTabs, ...primaryUserTabs];
    }
    return availableTabs;
  };
 
  const resolveTimelineHref = useCallback(async () => {
    try {
      const units = await fetchTimelineUnits();
      if (units.length > 0) {
        return `/timeline/${units[0].unitId}`;
      }
    } catch (error) {
      console.error("Error resolving timeline unit:", error);
    }
    return "/timeline";
  }, []);

  const onTabChange = async (tabName: any) => {
    setSelectedTab(tabName);
    setIsMobileMenuOpen(false);
    const tab = getAvailableTabs().find((t) => t.name === tabName);
    if (tab === undefined) return;

    navigate(tab.name === "Timeline" ? await resolveTimelineHref() : tab.href);
  };
 
  useEffect(() => {
    const tab = getAvailableTabs().find(
      (t) =>
        location.pathname === t.href ||
        location.pathname.startsWith(`${t.href}/`),
    ) || { name: "" };
    setSelectedTab(tab.name);
  }, [location.pathname, userInfo]);
 
  // Load user info from token
  useEffect(() => {
    const checkToken = () => {
      const token = localStorage.getItem("token");
      if (token) {
        const payload = decodeJWT(token);
        if (payload) {
          // Check if it's a Google token
          const isGoogleToken =
            payload.iss && payload.iss.includes("google.com");
 
          let isPrimaryUser = false; // Default to false
          if (isGoogleToken) {
            isPrimaryUser = true; // Google users are always primary
          } else if (payload.isPrimaryUser !== undefined) {
            isPrimaryUser = payload.isPrimaryUser.toLowerCase() === "true";
          }
 
          setUserInfo({
            id:
              payload.nameid ||
              payload.sub ||
              payload.id ||
              "authenticated-user",
            userName:
              payload.name || payload.unique_name || payload.userName || "User",
            isPrimaryUser: isPrimaryUser,
            token: token,
          });
        } else {
          // Assume Google auth (opaque token) - Google users are primary
          setUserInfo({
            id: "google-user",
            userName: "Google User",
            isPrimaryUser: true,
            token: token,
          });
        }
      } else {
        setUserInfo(null);
      }
    };
 
    checkToken(); // Check immediately on mount
    
    // Listen for token changes from other tabs or same tab
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === "token" || event.key === null) {
        // event.key === null means clear() was called
        checkToken();
      }
    };
    
    window.addEventListener("storage", handleStorageChange);
    
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);
 
  const performLogout = async () => {
    try {
      const response = await fetch(`${baseUrl}/api/auth/logout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
 
      if (!response.ok) {
        console.error("Logout failed with status:", response.status);
        // Still proceed with frontend cleanup even if backend fails
      } else {
        console.log("Logout successful");
      }
    } catch (error) {
      console.error("Logout network error:", error);
      // Still proceed with frontend cleanup
    }
 
    // Always clear frontend tokens and redirect
    localStorage.removeItem("token");
    localStorage.removeItem("expires_at");
    setToken(null);
    navigate("/");
  };
 
  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white px-4 sm:px-6 lg:px-8 border-b border-gray-200 fixed top-0 left-0 right-0 z-50">
        <div className="mx-auto">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <div className="flex-shrink-0 flex items-center">
                <img
                  className="h-8 w-8 sm:h-10 sm:w-10 rounded-lg shadow-sm"
                  src="/tda.png"
                  alt="TDA Logo"
                />
                <div className="ml-3">
                  <h1 className="text-lg sm:text-xl font-bold text-gray-900">
                    TDA
                  </h1>
                  <p className="text-xs text-gray-500 hidden sm:block">
                    Property Management
                  </p>
                </div>
              </div>
            </div>
 
            {/* Desktop Navigation - Show on lg screens and above */}
            <div className="hidden lg:flex lg:items-baseline lg:space-x-0.5 lg:ml-3">
              {(() => {
                const availableTabs = getAvailableTabs();
                const home = availableTabs.find((tab) => tab.name === "Home");
                const leaseTabs = availableTabs.filter((tab) =>
                  LEASE_GROUP_NAMES.includes(tab.name),
                );
                const otherTabs = availableTabs.filter(
                  (tab) =>
                    tab.name !== "Home" &&
                    !LEASE_GROUP_NAMES.includes(tab.name),
                );
                const leaseActive = leaseTabs.some(
                  (tab) => tab.name === selectedTab,
                );

                const renderTab = (tab: (typeof availableTabs)[number]) => (
                  <button
                    key={tab.name}
                    onClick={() => onTabChange(tab.name)}
                    className={classNames(
                      selectedTab === tab.name
                        ? "bg-blue-50 text-blue-700 border-blue-200"
                        : "text-gray-600 hover:text-gray-900 hover:bg-gray-50 border-transparent",
                      "px-2.5 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center space-x-1.5 border shrink-0",
                    )}
                  >
                    <tab.icon className="h-4 w-4" />
                    <span className="whitespace-nowrap">{tab.name}</span>
                  </button>
                );

                return (
                  <>
                    {home && renderTab(home)}

                    {leaseTabs.length > 0 && (
                      <Menu as="div" className="relative">
                        <Menu.Button
                          className={classNames(
                            leaseActive
                              ? "bg-blue-50 text-blue-700 border-blue-200"
                              : "text-gray-600 hover:text-gray-900 hover:bg-gray-50 border-transparent",
                            "px-2.5 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center space-x-1.5 border shrink-0",
                          )}
                        >
                          <KeyIcon className="h-4 w-4" />
                          <span>Lease</span>
                          <ChevronDownIcon className="h-3.5 w-3.5" />
                        </Menu.Button>
                        <Transition
                          as={Fragment}
                          enter="transition ease-out duration-100"
                          enterFrom="transform opacity-0 scale-95"
                          enterTo="transform opacity-100 scale-100"
                          leave="transition ease-in duration-75"
                          leaveFrom="transform opacity-100 scale-100"
                          leaveTo="transform opacity-0 scale-95"
                        >
                          <Menu.Items className="absolute left-0 z-10 mt-2 min-w-[200px] origin-top-left rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none">
                            {leaseTabs.map((tab) => (
                              <Menu.Item key={tab.name}>
                                {({ active }) => (
                                  <button
                                    onClick={() => onTabChange(tab.name)}
                                    className={classNames(
                                      active || selectedTab === tab.name
                                        ? "bg-gray-50 text-blue-700"
                                        : "text-gray-700",
                                      "flex w-full items-center space-x-2.5 px-3 py-2 text-sm",
                                    )}
                                  >
                                    <tab.icon className="h-4 w-4" />
                                    <span>{tab.name}</span>
                                  </button>
                                )}
                              </Menu.Item>
                            ))}
                          </Menu.Items>
                        </Transition>
                      </Menu>
                    )}

                    {otherTabs.map(renderTab)}
                  </>
                );
              })()}
            </div>
 
            {/* User Actions */}
            <div className="flex items-center space-x-4">
              {/* Desktop Logout Button - Show on lg screens and above */}
              <button
                onClick={performLogout}
                className="hidden lg:flex items-center space-x-2 bg-red-50 text-red-700 hover:bg-red-100 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 border border-red-200"
              >
                <ArrowRightOnRectangleIcon className="h-4 w-4" />
                <span>Logout</span>
              </button>
 
              {/* Mobile Menu Button - Show on screens smaller than lg */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden inline-flex items-center justify-center p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-all duration-200"
              >
                <div className="space-y-1">
                  <div
                    className={`w-5 h-0.5 bg-current transform transition-transform duration-200 ${
                      isMobileMenuOpen ? "rotate-45 translate-y-1.5" : ""
                    }`}
                  />
                  <div
                    className={`w-5 h-0.5 bg-current transition-opacity duration-200 ${
                      isMobileMenuOpen ? "opacity-0" : ""
                    }`}
                  />
                  <div
                    className={`w-5 h-0.5 bg-current transform transition-transform duration-200 ${
                      isMobileMenuOpen ? "-rotate-45 -translate-y-1.5" : ""
                    }`}
                  />
                </div>
              </button>
            </div>
          </div>
        </div>
 
        {/* Mobile Menu - Show on screens smaller than lg */}
        <div
          className={`lg:hidden transition-all duration-300 ease-in-out ${
            isMobileMenuOpen ? "max-h-screen opacity-100" : "max-h-0 opacity-0"
          } overflow-hidden`}
        >
          <div className="max-h-[calc(100vh-4rem)] overflow-y-auto px-2 pt-2 pb-3 space-y-1 bg-white border-t border-gray-200">
            {getAvailableTabs().map((tab) => (
              <button
                key={tab.name}
                onClick={() => onTabChange(tab.name)}
                className={classNames(
                  selectedTab === tab.name
                    ? "bg-blue-50 text-blue-700 border-l-4 border-blue-500"
                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-50 border-l-4 border-transparent",
                  "block px-3 py-3 text-base font-medium w-full text-left transition-all duration-200 flex items-center space-x-3",
                )}
              >
                <tab.icon className="h-5 w-5" />
                <span>{tab.name}</span>
              </button>
            ))}
 
            {/* Mobile Logout */}
            <button
              onClick={performLogout}
              className="w-full text-left px-3 py-3 text-base font-medium text-red-600 hover:text-red-700 hover:bg-red-50 transition-all duration-200 flex items-center space-x-3 border-l-4 border-transparent"
            >
              <ArrowRightOnRectangleIcon className="h-5 w-5" />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </nav>
 
      {/* Main Content */}
      <main className="pt-16">
        <div className="px-4 sm:px-6 lg:px-8 pt-2 pb-1">{children}</div>
      </main>
    </div>
  );
};
 
export default Navbar;
 
 